import { useEffect, useMemo, useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { formatDate } from 'date-fns'
import {
  AlertTriangle,
  Circle,
  CircleAlert,
  CircleCheck,
  LucidePlay,
  Play,
  RefreshCcw,
  Send,
  Square,
  TriangleAlert,
} from 'lucide-react'

import { cn } from '@fortem-monorepo/js-utilities'
import { formatShortDuration, toISO8601 } from '@fortem-monorepo/js-utilities'
import { getTMinus1 } from '@fortem-monorepo/js-utilities'
import { isAnomaly } from '@fortem-monorepo/js-utilities'
import { groupByKey } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { LARGE_THEME } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { Spinner } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Skeleton } from '@fortem-monorepo/react-ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '@fortem-monorepo/react-ui'
import { BatchExecutor } from '@fortem-monorepo/web-sdk'

import { BatchExecutorStatusCellRenderer } from '@/ag-grid/cell-renderers/BatchExecutorStatusCellRenderer'
import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { RollRiskModal } from '@/components/dialogs/RollRiskModal'
import { RunRiskCalibrationModal } from '@/components/dialogs/RunRiskCalibrationModal'
import { SendBearScenarioModal } from '@/components/dialogs/SendBearScenarioModal'
import { OPERATIONS } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import { ReportDateButton } from '@/pages/operations/risk-callibration/components/ReportDateButton'
import {
  useRebootCloudPricingMutation,
  useStartCloudPricingMutation,
  useStopCloudPricingMutation,
} from '@/react-query/mutations/batch-executor'
import { useCloudPricingState } from '@/react-query/queries/risk'

const groupProcessDurations = (processDurations: Array<BatchExecutor.ProcessDurationItem>) => {
  return processDurations.reduce(
    (prev, curr) => {
      const _next = { ...prev }
      if (!_next[curr.fundname]) _next[curr.fundname ?? curr.type] = {}
      _next[curr.fundname][curr.type] = curr.time_taken_minutes
      return _next
    },
    {} as Record<string, Record<string, number>>,
  )
}

const createReportColDefs = (
  reportIsAnomaly: (report: BatchExecutor.ReportItem) => boolean,
  groupedProcessDurations: ReturnType<typeof groupProcessDurations> | undefined,
) => {
  return createColDefs<BatchExecutor.ReportItem>([
    {
      field: 'id',
      hide: true,
    },
    {
      field: 'fundname',
      cellClass: 'font-semibold',
    },
    { field: 'type' },
    {
      field: 'uploadtimestamp',
      headerName: 'Uploaded',
      valueFormatter: (params) => {
        if (params.value) {
          return formatDate(params?.value, 'HH:mm:ss aa')
        }
        return ''
      },
    },
    {
      field: 'executiontimestamp',
      headerName: 'Executed',
      valueFormatter: (params) => {
        if (params.value) {
          return formatDate(params?.value, 'HH:mm:ss aa')
        }
        return ''
      },
    },
    {
      field: 'completiontimestamp',
      headerName: 'Completed',
      valueFormatter: (params) => {
        if (params.value) {
          return formatDate(params?.value, 'HH:mm:ss aa')
        }
        return ''
      },
    },
    {
      field: 'crashed',
      headerName: 'Status',
      cellRenderer: BatchExecutorStatusCellRenderer,
    },
    {
      field: 'time_taken_minutes',
      headerName: 'Duration',
      cellRenderer: (params: any) => {
        return (
          <div className="w-full h-full flex gap-2 items-center">
            {formatShortDuration(params?.value)}
            {reportIsAnomaly(params.node.data) && groupedProcessDurations && (
              <Tooltip>
                <TooltipTrigger>
                  <AlertTriangle className={'text-amber-500'} size={20} />
                </TooltipTrigger>
                <TooltipContent>
                  Average time for
                  <br /> {params.node.data.fundname} - {params.node.data.type}
                  <br />
                  {formatShortDuration(
                    groupedProcessDurations[params.node.data.fundname ?? ''][params.node.data.type],
                  )}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )
      },
    },
  ])
}

const instanceQueueColDefs = createColDefs([
  {
    field: 'id',
  },
  {
    field: 'instancetag',
  },
  {
    field: 'createdby',
  },
  {
    field: 'uploadtimestamp',
    valueFormatter: (params) => formatDate(params?.value, 'HH:mm:ss aa'),
  },
  {
    field: 'command',
  },
  {
    field: 'executed',
  },
  {
    field: 'completed',
    cellRenderer: (params: any) => {
      return (
        <div className={'w-full h-full flex items-center'}>
          {params?.value ? (
            <CircleCheck size={20} className={'text-success'} />
          ) : params.node.data.crashed ? (
            <CircleAlert size={20} className={'text-destructive'} />
          ) : (
            <></>
          )}
        </div>
      )
    },
  },
  { field: 'crashed' },
  {
    field: 'completiontimestamp',
    valueFormatter: (params) => formatDate(params?.value, 'HH:mm:ss aa'),
  },
  {
    field: 'executiontimestamp',
    valueFormatter: (params) => formatDate(params?.value, 'HH:mm:ss aa'),
  },
  {
    field: 'crashtimestamp',
    valueFormatter: (params) => formatDate(params?.value, 'HH:mm:ss aa'),
  },
  {
    field: 'username',
  },
  {
    field: 'email',
  },
  {
    field: 'shutdownafterexecution',
  },
  {
    field: 'batchid1',
    hide: true,
  },
  {
    field: 'batchid2',
    hide: true,
  },
  {
    field: 'command_type',
  },
])

export interface RiskCallibrationPageProps {}

export const RiskCallibrationPage: React.FC<RiskCallibrationPageProps> = ({}) => {
  const api = useApi()

  const [selectedDate, setSelectedDate] = useState(toISO8601(getTMinus1()))

  const queries = useQueries({
    queries: [
      {
        queryKey: ['batch-executor/report-progress'],
        queryFn: () => api.batchExecutor.getReportProgress(),
      },
      {
        queryKey: ['batch-executor/process-durations'],
        queryFn: () => api.batchExecutor.getProcessDurations(),
      },
      {
        queryKey: ['batch-executor/instance-queue'],
        queryFn: () => api.batchExecutor.getInstanceQueues(),
      },
    ],
  })

  const { query: cloudPricing, cloudPricingStatus } = useCloudPricingState()

  const [reports, processDurations, instanceQueues] = queries

  const reportData = useMemo(() => {
    return (reports.data ?? []).filter((report) => !!report.uploadtimestamp)
  }, [reports.data])

  const instanceQueueData = useMemo(() => {
    return instanceQueues.data ?? []
  }, [instanceQueues.data])

  const processDurationsData = useMemo(() => {
    return processDurations.data ?? []
  }, [processDurations.data])

  const groupedReportProgress = useMemo(() => groupByKey(reportData, 'batchdate'), [reportData])
  const groupedInstanceQueues = useMemo(
    () => groupByKey(instanceQueueData, 'uploadtimestamp', (timestamp) => timestamp.split('T')[0]),
    [instanceQueueData],
  )

  const groupedProcessDurations = useMemo(
    () => (processDurationsData.length ? groupProcessDurations(processDurationsData) : undefined),
    [processDurationsData],
  )

  const visibleReports = useMemo(() => {
    return groupedReportProgress[selectedDate] ?? []
  }, [groupedReportProgress, selectedDate])

  useEffect(() => {
    if (
      groupedReportProgress &&
      !groupedReportProgress[selectedDate] &&
      Object.keys(groupedReportProgress)[0]
    ) {
      setSelectedDate(Object.keys(groupedReportProgress)?.[0])
    }
  }, [groupedReportProgress])

  const reportIsAnomaly = (report: BatchExecutor.ReportItem) => {
    const _isAnomaly = groupedProcessDurations
      ? isAnomaly(
          report.time_taken_minutes,
          groupedProcessDurations[report.fundname ?? ''][report.type],
          30,
        )
      : false
    return _isAnomaly
  }

  const startCloudPricingMutation = useStartCloudPricingMutation()
  const stopCloudPricingMutation = useStopCloudPricingMutation()
  const rebootCloudPricingMutation = useRebootCloudPricingMutation()

  const [open, setOpen] = useState(false)
  const [sendEmailOpen, setSendEmailOpen] = useState(false)
  const [rollRiskOpen, setRollRiskOpen] = useState(false)

  return (
    <Page metadata={{ title: 'Risk Calibration' }} permissions={OPERATIONS.RISK.permissions}>
      <SendBearScenarioModal open={sendEmailOpen} onOpenChange={setSendEmailOpen} />
      <RunRiskCalibrationModal
        open={open}
        onOpenChange={setOpen}
        awsActive={cloudPricingStatus === 'RUNNING'}
      />
      <RollRiskModal open={rollRiskOpen} onOpenChange={setRollRiskOpen} />
      <HeaderConfigBar className={'px-0!'}>
        <div className={cn('relative overflow-x-hidden', 'w-full h-21.5 px-4!')}>
          <div
            className={cn('absolute top-0 left-0 w-full h-full overflow-x-scroll ', {
              ['pointer-events-none']: reports.isFetching,
            })}
          >
            <div className={cn('grid grid-flow-col gap-4 w-full h-full px-4 z-10')}>
              {reports.isFetching
                ? Array.from({ length: 20 }).map((_, index) => (
                    <Skeleton className={'w-27 h-full'} key={index} />
                  ))
                : groupedReportProgress &&
                  Object.entries(groupedReportProgress)
                    .filter(([date, _reports]) => {
                      return date !== 'null'
                    })
                    .map(
                      ([date, reports]) =>
                        date !== 'NULL' && (
                          <ReportDateButton
                            key={date}
                            date={date}
                            reports={reports}
                            reportIsAnomaly={reportIsAnomaly}
                            active={selectedDate === date}
                            onClick={() => setSelectedDate(date)}
                          />
                        ),
                    )}
            </div>
          </div>
        </div>
      </HeaderConfigBar>
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <Button size="sm" variant={'outline'} onClick={() => setSendEmailOpen(true)}>
            <Send />
            Bear Scenario
          </Button>
          <CanAccess permissions={[PERMISSIONS.WRITE_CLOUD_PRICING]}>
            <div className="flex items-center gap-2">
              <span className={'text-sm font-medium'}>Cloud Pricing</span>
              <span
                className={cn('flex items-center gap-2 font-medium text-sm  px-3 py-1 rounded-md', {
                  'text-gray-600 bg-gray-200': true,
                  'text-red-600 bg-red-100': cloudPricing.error || cloudPricingStatus == 'ERROR',
                  'text-green-600 bg-green-100': cloudPricingStatus === 'RUNNING',
                  'text-orange-600 bg-orange-100': cloudPricingStatus === 'STOPPING',
                })}
              >
                Status:
                {` ${cloudPricingStatus.toLowerCase().replaceAll('_', ' ')}`}
                {cloudPricing.isFetching && <Spinner size={16} className={'size-4'} />}
                {!cloudPricing.isFetching && cloudPricingStatus === 'RUNNING' && (
                  <Circle
                    size={14}
                    className={'animate-pulse text-green-600 fill-green-600 duration-1500'}
                  />
                )}
                {!cloudPricing.isFetching && cloudPricingStatus === 'ERROR' && (
                  <TriangleAlert size={16} />
                )}
              </span>
              <LoadingButton
                size="sm"
                variant={'outline'}
                disabled={['ERROR', 'TERMINATED', 'SHUTTING_DOWN', 'PENDING', 'RUNNING'].includes(
                  cloudPricingStatus,
                )}
                onClick={() => startCloudPricingMutation.mutateAsync()}
                icon={<Play />}
              >
                Start
              </LoadingButton>
              <LoadingButton
                size="sm"
                variant={'outline'}
                disabled={['ERROR', 'TERMINATED', 'SHUTTING_DOWN', 'PENDING', 'STOPPED'].includes(
                  cloudPricingStatus,
                )}
                onClick={() => stopCloudPricingMutation.mutateAsync()}
                icon={<Square />}
              >
                Stop
              </LoadingButton>
              <LoadingButton
                size="sm"
                variant={'outline'}
                disabled={['ERROR', 'TERMINATED', 'SHUTTING_DOWN', 'STOPPED', 'PENDING'].includes(
                  cloudPricingStatus,
                )}
                onClick={() => rebootCloudPricingMutation.mutateAsync()}
                icon={<RefreshCcw />}
              >
                Reboot
              </LoadingButton>
            </div>
          </CanAccess>
          <CanAccess permissions={[PERMISSIONS.RUN_RISK_CALIBRATION]}>
            <Button size="sm" variant={'outline'} onClick={() => setOpen(true)}>
              <LucidePlay />
              Run Jobs
            </Button>
          </CanAccess>
          <CanAccess permissions={[PERMISSIONS.WRITE_RISK_CALIBRATION]}>
            <Button size="sm" variant={'outline'} onClick={() => setRollRiskOpen(true)}>
              <LucidePlay />
              Roll Risk
            </Button>
          </CanAccess>
        </div>
      </HeaderConfigBar>
      <HeaderConfigBar>
        <div className={'font-semibold'}>{formatDate(selectedDate, 'EEEE do LLLL yyyy')}</div>
      </HeaderConfigBar>
      <div className={'flex-1'}>
        <ResizablePanelGroup direction={'horizontal'}>
          <ResizablePanel className={''}>
            <Widget exposed className={'h-full'}>
              <WidgetHeaderWithQuery query={reports} title="Reports" />
              <WidgetContent loading={reports.isLoading}>
                <CustomAgGrid
                  domLayout={'normal'}
                  columnDefs={createReportColDefs(reportIsAnomaly, groupedProcessDurations)}
                  rowData={visibleReports}
                  rowClassRules={{
                    ['bg-red-50/70!']: (params: any) => params.data.crashed,
                    ['bg-amber-50!']: (params: any) => {
                      return reportIsAnomaly(params.data)
                    },
                  }}
                  theme={LARGE_THEME}
                />
              </WidgetContent>
            </Widget>
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel>
            <Widget exposed className={'h-full'}>
              <WidgetHeaderWithQuery query={instanceQueues} title="Instance Queue" />
              <WidgetContent loading={instanceQueues?.isLoading}>
                <CustomAgGrid
                  domLayout={'normal'}
                  columnDefs={instanceQueueColDefs}
                  rowData={groupedInstanceQueues[selectedDate] ?? []}
                />
              </WidgetContent>
            </Widget>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </Page>
  )
}
